import { REGION_MIN_R7_JUL } from '../data/prefectures';
import { judgeMeasure2 } from '../lib/calc';
import type { Category, PlanInputs } from '../types';
import { NumberField } from './NumberField';

interface Props {
  inputs: PlanInputs;
  onChange: (patch: Partial<PlanInputs>) => void;
}

const APPLICABLE_CATEGORIES: Category[] = ['A_yes', 'B'];

export function KatenMeasures({ inputs, onChange }: Props) {
  const applicable = APPLICABLE_CATEGORIES.includes(inputs.category);
  const r7 = REGION_MIN_R7_JUL[inputs.prefectureName] ?? null;
  const measure2Auto = judgeMeasure2(inputs.siteMin, r7);

  // 加点措置① 自動判定: 地域別最低賃金未満で雇用された従業員数 / 全従業員数 ≥ 30%
  const ratio =
    inputs.measure1TotalCount > 0
      ? inputs.measure1BelowCount / inputs.measure1TotalCount
      : 0;
  const measure1Auto = inputs.measure1TotalCount > 0 && ratio >= 0.3;
  const ratioPct = (ratio * 100).toFixed(1);

  return (
    <div className="section">
      <h2>
        <span className="num">★</span>追加の加点措置（自動判定）
      </h2>
      <p className="section-desc">
        画面上の加点措置①・②に該当するか、入力内容から<b>自動判定</b>します。判定結果が「該当」の場合、システム入力時に該当チェックを入れてください。
      </p>

      {!applicable && (
        <div className="tip-box">
          <span className="lbl">
            <i className="fas fa-info-circle" /> 注：
          </span>
          現在の事業者区分（区分A・加点希望なし）では、加点措置①②は適用されません。
        </div>
      )}

      <h3>加点措置①：地域別最低賃金未満の雇用者比率</h3>
      <div className="condition-box">
        <div className="condition-label">
          <i className="fas fa-bullseye" /> 加点条件
        </div>
        <div className="condition-text">
          指定期間（<b>R6.10〜R7.9</b>）に<b>3か月以上</b>、地域別最低賃金未満で雇用していた従業員の比率が <b className="condition-emph">30%以上</b>
        </div>
      </div>
      <div className="form-row">
        <div className="label">
          指定期間中の全従業員数<small>（最大値）</small>
        </div>
        <div>
          <NumberField
            value={inputs.measure1TotalCount}
            integer
            min={0}
            onChange={(measure1TotalCount) => onChange({ measure1TotalCount })}
          />
          <span className="unit">人</span>
        </div>
      </div>
      <div className="form-row">
        <div className="label">
          うち地域別最低賃金未満で<br />3か月以上雇用<small>R6.10〜R7.9</small>
        </div>
        <div>
          <NumberField
            value={inputs.measure1BelowCount}
            integer
            min={0}
            max={inputs.measure1TotalCount}
            onChange={(measure1BelowCount) => onChange({ measure1BelowCount })}
          />
          <span className="unit">人</span>
        </div>
      </div>
      <div className={`judge-box ${applicable && measure1Auto ? 'ok' : 'warn'}`} style={{ marginTop: '0.6rem' }}>
        <div className="ttl">
          <i className={`fas ${measure1Auto ? 'fa-check-circle' : 'fa-times-circle'}`} />{' '}
          自動判定：{measure1Auto ? '該当（加点措置①取得可）' : '非該当'}
        </div>
        <div className="desc">
          比率 = {inputs.measure1BelowCount} / {inputs.measure1TotalCount || 0} ={' '}
          <b>{ratioPct}%</b>　（基準：30.0%以上）
        </div>
      </div>

      <h3 style={{ marginTop: '1.2rem' }}>加点措置②：事業場内最低賃金が R7.7時点 +63円以上</h3>
      <div className="condition-box">
        <div className="condition-label">
          <i className="fas fa-bullseye" /> 加点条件
        </div>
        <div className="condition-text">
          交付申請の直近月における<b>事業場内最低賃金</b>が、<b className="condition-emph">R7.7時点の地域別最低賃金 + 63円以上</b>
        </div>
      </div>
      {r7 !== null ? (
        <div className="form-row">
          <div className="label">
            判定基準
            <small>{inputs.prefectureName} R7.7時点 = {r7.toLocaleString()}円</small>
          </div>
          <div>
            <span style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 600 }}>
              {r7.toLocaleString()} + 63 = <b>{(r7 + 63).toLocaleString()}円以上</b>
            </span>
          </div>
        </div>
      ) : (
        <div className="tip-box">
          <span className="lbl">
            <i className="fas fa-info-circle" /> 参考値です：
          </span>
          {inputs.prefectureName} の R7.7時点最低賃金（令和6年度改定値）は参考値として収録しています。最終判断は最新の公募要領で必ずご確認ください。
        </div>
      )}
      <div className="hint" style={{ marginTop: '0.4rem', fontSize: '0.78rem' }}>
        <i className="fas fa-info-circle" /> R7.7時点の値は<b>令和6年度改定（2024年10月〜2025年9月適用）</b>の地域別最低賃金です。最新の公募要領で必ずご確認ください。
      </div>
      <div
        className={`judge-box ${applicable && measure2Auto ? 'ok' : 'warn'}`}
        style={{ marginTop: '0.6rem' }}
      >
        <div className="ttl">
          <i className={`fas ${measure2Auto ? 'fa-check-circle' : 'fa-times-circle'}`} />{' '}
          自動判定：{r7 === null ? '判定不能（基準データなし）' : measure2Auto ? '該当（加点措置②取得可）' : '非該当'}
        </div>
        <div className="desc">
          事業場内最低賃金 <b>{inputs.siteMin.toLocaleString()}円</b>
          {r7 !== null && (
            <>
              　vs 基準 <b>{(r7 + 63).toLocaleString()}円</b>
              （差 {(inputs.siteMin - (r7 + 63)).toLocaleString()}円）
            </>
          )}
        </div>
      </div>

      {applicable && (
        <details style={{ marginTop: '1rem' }}>
          <summary style={{ cursor: 'pointer', fontWeight: 600, color: 'var(--g700)' }}>
            手動上書き（自動判定を無視して申請する場合）
          </summary>
          <div className="form-row checkbox-row" style={{ marginTop: '0.5rem' }}>
            <div className="label">加点措置① 手動チェック</div>
            <div>
              <label className="check-label">
                <input
                  type="checkbox"
                  checked={inputs.measure1Eligible}
                  onChange={(e) => onChange({ measure1Eligible: e.target.checked })}
                />
                該当（手動指定）
              </label>
            </div>
          </div>
          <div className="form-row checkbox-row">
            <div className="label">加点措置② 手動チェック</div>
            <div>
              <label className="check-label">
                <input
                  type="checkbox"
                  checked={inputs.measure2Eligible}
                  onChange={(e) => onChange({ measure2Eligible: e.target.checked })}
                />
                該当（手動指定）
              </label>
            </div>
          </div>
        </details>
      )}
    </div>
  );
}
